import React, { useState, useEffect, useMemo } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { workspaceAPI } from '../services/api';
import { useBranding, saveBranding, DEFAULT_BRANDING, getContrastTextColor, applyBrandingToDocument } from '../contexts/BrandingContext';

// ════════════════════════════════════════════════════════════════════════════
// Branding cabinet — personnalisation white-label
// ════════════════════════════════════════════════════════════════════════════
// Permet à chaque client (cabinet BtoB / cabinet d'avocat / fiduciaire) de
// configurer son identité visuelle, qui sera ensuite injectée dans :
//   - le header de son espace Lexback (logo + nom commercial)
//   - les emails sortants envoyés à ses propres clients via la plateforme
//   - le portail "client final" si activé
//
// Stockage :
//   - PATCH /api/workspace/me { branding: {...} }
//   - localStorage en miroir (lexback_branding) pour appliquer instantanément
//     via le BrandingProvider sans attendre le round-trip serveur.
// ════════════════════════════════════════════════════════════════════════════

const FONT_OPTIONS = [
  { value: 'Syne',       label: 'Syne (par défaut Lexback)' },
  { value: 'Inter',      label: 'Inter — moderne, sobre' },
  { value: 'Playfair Display', label: 'Playfair Display — élégant, sérieux' },
  { value: 'DM Sans',    label: 'DM Sans — humaniste' },
  { value: 'Manrope',    label: 'Manrope — tech, neutre' },
  { value: 'IBM Plex Sans', label: 'IBM Plex Sans — institutionnel' },
];

const PRESET_PALETTES = [
  { name: 'Lexback (défaut)', primaire: '#B8860B', secondaire: '#1A1916', accent: '#1A55A0' },
  { name: 'Avocat classique', primaire: '#1A3A6E', secondaire: '#0A0A0A', accent: '#9F8651' },
  { name: 'Notaire / Officier',primaire: '#6B2A2A', secondaire: '#2A2A2A', accent: '#D4A857' },
  { name: 'Expert-comptable',  primaire: '#0F766E', secondaire: '#1F2937', accent: '#0EA5E9' },
  { name: 'LegalTech moderne', primaire: '#7C3AED', secondaire: '#111827', accent: '#EC4899' },
  { name: 'Sobre & minimal',   primaire: '#000000', secondaire: '#000000', accent: '#666666' },
];

// ── Helpers locaux ─────────────────────────────────────────────────────────
const fileToDataURL = (file) => new Promise((resolve, reject) => {
  const r = new FileReader();
  r.onload = () => resolve(r.result);
  r.onerror = () => reject(new Error('Lecture du fichier échouée'));
  r.readAsDataURL(file);
});

const isValidHex = (s) => /^#([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/.test((s || '').trim());

// ── Composant principal ────────────────────────────────────────────────────
export default function Branding() {
  const auth = useAuth() || {};
  const user = auth.user || null;
  const { branding: ctxBranding, setBranding: setCtxBranding } = useBranding();
  // État local de travail (édité par les inputs). On le synchronise avec le
  // context au chargement, et on diffuse via setCtxBranding au Save.
  const [branding, setBranding] = useState(ctxBranding || DEFAULT_BRANDING);
  const [initial, setInitial] = useState(ctxBranding || DEFAULT_BRANDING);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState('');

  // Si le context change (autre source, refresh), sync l'état local non modifié
  useEffect(() => {
    setBranding(ctxBranding);
    setInitial(ctxBranding);
  }, [ctxBranding]);

  // Best-effort : charge depuis le workspace (override le context si présent)
  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const res = await workspaceAPI.get();
        const ws = res?.data || {};
        const remoteBranding = ws.branding || null;
        if (remoteBranding && mounted) {
          const merged = { ...DEFAULT_BRANDING, ...remoteBranding };
          setBranding(merged);
          setInitial(merged);
          setCtxBranding(merged); // diffuse à toute l'app
        }
      } catch (e) {
        console.warn('[Branding] workspace fetch failed (using local)', e?.message || e);
      }
    })();
    return () => { mounted = false; };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const isDirty = useMemo(() => JSON.stringify(branding) !== JSON.stringify(initial), [branding, initial]);

  const update = (patch) => {
    setBranding(b => ({ ...b, ...patch }));
    setSaved(false);
    setError('');
  };

  // ── Upload logo (data URL pour démo, à remplacer par upload R2 côté backend) ──
  const handleLogoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) {
      setError('Le logo doit faire moins de 2 Mo.');
      return;
    }
    try {
      const dataUrl = await fileToDataURL(file);
      update({ logo_url: dataUrl });
    } catch (err) {
      setError('Impossible de lire le fichier.');
    }
  };
  const handleFaviconUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 512 * 1024) {
      setError('La favicon doit faire moins de 512 Ko.');
      return;
    }
    try {
      const dataUrl = await fileToDataURL(file);
      update({ favicon_url: dataUrl });
    } catch (err) {
      setError('Impossible de lire le fichier.');
    }
  };

  // ── Sauvegarde ──
  const handleSave = async () => {
    setSaving(true);
    setError('');
    // 1) Diffusion locale via le context (écrit localStorage + applique CSS vars
    //    + déclenche 'branding:updated' pour tous les abonnés de l'app)
    try {
      setCtxBranding(branding);
      setInitial(branding);
      setSaved(true);
      setTimeout(() => setSaved(false), 2500);
    } catch (e) {
      console.error('[Branding] local save failed', e);
    }
    // 2) Backend (workspace.branding) — best effort, n'écrase pas le succès local
    try {
      await workspaceAPI.update({ branding });
    } catch (err) {
      console.warn('[Branding] backend save failed', err);
      setError("Sauvegardé localement. La synchro serveur n'a pas répondu (CORS / endpoint manquant côté API).");
    } finally {
      setSaving(false);
    }
  };

  const handleReset = () => {
    if (!window.confirm('Remettre les paramètres Lexback par défaut ? Cette action est immédiate.')) return;
    // Diffuse immédiatement via le context — la sidebar et le UI reviennent
    // instantanément à la couleur or Lexback, et localStorage est mis à jour.
    setBranding(DEFAULT_BRANDING);
    setCtxBranding(DEFAULT_BRANDING);
    setInitial(DEFAULT_BRANDING);
    setSaved(true);
    setTimeout(() => setSaved(false), 2000);
    // Best-effort backend
    workspaceAPI.update({ branding: DEFAULT_BRANDING }).catch(err => {
      console.warn('[Branding] backend reset failed', err);
    });
  };

  if (loading) {
    return <div style={{ padding:'3rem', textAlign:'center', color:'rgba(26,25,22,0.4)', fontSize:'13px' }}>Chargement…</div>;
  }

  return (
    <div style={{ paddingBottom:'2rem' }}>
      {/* Header */}
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:'1.5rem' }}>
        <div>
          <div style={{ fontFamily:'Syne, sans-serif', fontSize:'18px', fontWeight:600, color:'#1A1916' }}>
            Identité visuelle de mon cabinet
          </div>
          <div style={{ fontSize:'12px', color:'rgba(26,25,22,0.4)', marginTop:'2px' }}>
            Personnalisez votre espace, vos communications et le portail de vos clients
          </div>
        </div>
        <div style={{ display:'flex', gap:'8px' }}>
          <button
            onClick={handleReset}
            disabled={saving}
            style={{ background:'transparent', border:'0.5px solid rgba(0,0,0,0.15)', color:'#5C6B7A', padding:'8px 14px', borderRadius:'7px', fontSize:'12.5px', cursor: saving ? 'not-allowed' : 'pointer' }}
          >
            Réinitialiser
          </button>
          <button
            onClick={handleSave}
            disabled={!isDirty || saving}
            style={{
              background: !isDirty || saving ? 'rgba(184,134,11,0.5)' : '#B8860B',
              color:'white', border:'none',
              padding:'8px 18px', borderRadius:'7px', fontSize:'12.5px', fontWeight:500,
              cursor: !isDirty || saving ? 'not-allowed' : 'pointer',
            }}
          >
            {saving ? 'Enregistrement…' : saved ? '✓ Enregistré' : 'Enregistrer'}
          </button>
        </div>
      </div>

      {error && (
        <div style={{ background:'rgba(176,48,32,0.06)', border:'0.5px solid rgba(176,48,32,0.20)', color:'#B03020', padding:'10px 14px', borderRadius:'8px', fontSize:'12.5px', marginBottom:'1rem' }}>
          {error}
        </div>
      )}

      <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'1.25rem' }}>

        {/* ═════════ Colonne gauche : édition ═════════ */}
        <div style={{ display:'flex', flexDirection:'column', gap:'1rem' }}>

          {/* ── Logo ── */}
          <Card title="Logo & favicon">
            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'12px' }}>
              {/* Logo */}
              <div>
                <Label>Logo principal</Label>
                <div style={{
                  border:'1px dashed rgba(0,0,0,0.18)', borderRadius:'8px',
                  background:'#fafaf8',
                  padding:'14px',
                  textAlign:'center',
                  display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center',
                  gap:'8px',
                  minHeight:'100px',
                }}>
                  {branding.logo_url ? (
                    <>
                      <img src={branding.logo_url} alt="Logo" style={{ maxWidth:'140px', maxHeight:'56px', objectFit:'contain' }} />
                      <div style={{ display:'flex', gap:'6px' }}>
                        <label style={uploadBtn}>Remplacer<input type="file" accept="image/*" onChange={handleLogoUpload} style={{ display:'none' }} /></label>
                        <button onClick={() => update({ logo_url:'' })} style={removeBtn}>Retirer</button>
                      </div>
                    </>
                  ) : (
                    <>
                      <span style={{ fontSize:'24px', opacity:0.4 }}>🖼</span>
                      <div style={{ fontSize:'10.5px', color:'#9a8f7a' }}>PNG, JPG ou SVG · max 2 Mo</div>
                      <label style={uploadBtn}>Ajouter un logo<input type="file" accept="image/*" onChange={handleLogoUpload} style={{ display:'none' }} /></label>
                    </>
                  )}
                </div>
              </div>
              {/* Favicon */}
              <div>
                <Label>Favicon (onglet navigateur)</Label>
                <div style={{
                  border:'1px dashed rgba(0,0,0,0.18)', borderRadius:'8px',
                  background:'#fafaf8',
                  padding:'14px',
                  textAlign:'center',
                  display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center',
                  gap:'8px',
                  minHeight:'100px',
                }}>
                  {branding.favicon_url ? (
                    <>
                      <img src={branding.favicon_url} alt="Favicon" style={{ width:'32px', height:'32px', objectFit:'contain' }} />
                      <div style={{ display:'flex', gap:'6px' }}>
                        <label style={uploadBtn}>Remplacer<input type="file" accept="image/*" onChange={handleFaviconUpload} style={{ display:'none' }} /></label>
                        <button onClick={() => update({ favicon_url:'' })} style={removeBtn}>Retirer</button>
                      </div>
                    </>
                  ) : (
                    <>
                      <span style={{ fontSize:'20px', opacity:0.4 }}>◯</span>
                      <div style={{ fontSize:'10.5px', color:'#9a8f7a' }}>PNG ou ICO · 32×32 idéal · max 512 Ko</div>
                      <label style={uploadBtn}>Ajouter<input type="file" accept="image/*" onChange={handleFaviconUpload} style={{ display:'none' }} /></label>
                    </>
                  )}
                </div>
              </div>
            </div>
          </Card>

          {/* ── Couleurs ── */}
          <Card title="Couleurs">
            <div style={{ display:'grid', gridTemplateColumns:'repeat(3, 1fr)', gap:'10px', marginBottom:'12px' }}>
              <ColorField label="Primaire"    value={branding.couleur_primaire}    onChange={v => update({ couleur_primaire: v })}    hint="Boutons, accents principaux" />
              <ColorField label="Secondaire"  value={branding.couleur_secondaire}  onChange={v => update({ couleur_secondaire: v })}  hint="Texte, headers" />
              <ColorField label="Accent"      value={branding.couleur_accent}      onChange={v => update({ couleur_accent: v })}      hint="Liens, badges info" />
            </div>
            <Label>Palettes pré-définies</Label>
            <div style={{ display:'grid', gridTemplateColumns:'repeat(2, 1fr)', gap:'6px', marginTop:'4px' }}>
              {PRESET_PALETTES.map(p => (
                <button
                  key={p.name}
                  onClick={() => update({ couleur_primaire: p.primaire, couleur_secondaire: p.secondaire, couleur_accent: p.accent })}
                  style={{
                    background:'#fff', border:'0.5px solid rgba(0,0,0,0.12)', borderRadius:'7px',
                    padding:'8px 10px', cursor:'pointer', fontFamily:'inherit', textAlign:'left',
                    display:'flex', alignItems:'center', gap:'8px',
                  }}
                  title={`${p.primaire} / ${p.secondaire} / ${p.accent}`}
                >
                  <div style={{ display:'flex', gap:'2px' }}>
                    {[p.primaire, p.secondaire, p.accent].map((c, i) => (
                      <span key={i} style={{ width:'14px', height:'14px', borderRadius:'3px', background:c, border:'0.5px solid rgba(0,0,0,0.15)' }} />
                    ))}
                  </div>
                  <span style={{ fontSize:'11.5px', color:'#1A1916' }}>{p.name}</span>
                </button>
              ))}
            </div>
          </Card>

          {/* ── Typographie ── */}
          <Card title="Typographie">
            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'10px' }}>
              <div>
                <Label>Police des titres</Label>
                <select value={branding.font_titre} onChange={e => update({ font_titre: e.target.value })} style={selectStyle}>
                  {FONT_OPTIONS.map(f => <option key={f.value} value={f.value}>{f.label}</option>)}
                </select>
              </div>
              <div>
                <Label>Police du corps</Label>
                <select value={branding.font_corps} onChange={e => update({ font_corps: e.target.value })} style={selectStyle}>
                  {FONT_OPTIONS.map(f => <option key={f.value} value={f.value}>{f.label}</option>)}
                </select>
              </div>
            </div>
          </Card>

          {/* ── Identité ── */}
          <Card title="Identité du cabinet">
            <div style={{ display:'grid', gridTemplateColumns:'1fr', gap:'10px' }}>
              <div>
                <Label>Nom commercial affiché</Label>
                <input type="text" value={branding.nom_commercial} onChange={e => update({ nom_commercial: e.target.value })} placeholder="Ex : Cabinet Dupont & Associés" style={inputStyle} />
                <Hint>Remplace "Lexback" dans votre espace et vos communications.</Hint>
              </div>
              <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'10px' }}>
                <div>
                  <Label>Site web</Label>
                  <input type="url" value={branding.url_site} onChange={e => update({ url_site: e.target.value })} placeholder="https://" style={inputStyle} />
                </div>
                <div>
                  <Label>Téléphone support</Label>
                  <input type="tel" value={branding.telephone_support} onChange={e => update({ telephone_support: e.target.value })} placeholder="01 23 45 67 89" style={inputStyle} />
                </div>
              </div>
              <div>
                <Label>Email expéditeur (envois automatiques)</Label>
                <input type="email" value={branding.email_expediteur} onChange={e => update({ email_expediteur: e.target.value })} placeholder="contact@moncabinet.fr" style={inputStyle} />
                <Hint>Vérification DNS requise avant activation.</Hint>
              </div>
              <div>
                <Label>Signature email</Label>
                <textarea value={branding.signature_email} onChange={e => update({ signature_email: e.target.value })} placeholder="Cabinet Dupont &#10;25 rue de la République, 75001 Paris" rows={3} style={{ ...inputStyle, resize:'vertical', minHeight:'60px', lineHeight:1.5 }} />
              </div>
            </div>
          </Card>

          {/* ── Mode white-label ── */}
          <Card title="Mode white-label">
            <label style={{ display:'flex', alignItems:'flex-start', gap:'10px', cursor:'pointer' }}>
              <input type="checkbox" checked={branding.hide_lexback_branding} onChange={e => update({ hide_lexback_branding: e.target.checked })} style={{ marginTop:'2px' }} />
              <div>
                <div style={{ fontSize:'12.5px', color:'#1A1916', fontWeight:500 }}>
                  Masquer les mentions Lexback dans mes communications
                </div>
                <div style={{ fontSize:'11.5px', color:'#9a8f7a', marginTop:'2px', lineHeight:1.5 }}>
                  Les emails, PDF et le portail client n'afficheront plus "Powered by Lexback". Disponible à partir du plan <strong style={{ color:'#B8860B' }}>Business</strong>.
                </div>
              </div>
            </label>
          </Card>

        </div>

        {/* ═════════ Colonne droite : aperçu ═════════ */}
        <div style={{ position:'sticky', top:'20px', alignSelf:'flex-start' }}>
          <div style={{ fontSize:'11px', textTransform:'uppercase', letterSpacing:'0.07em', color:'rgba(26,25,22,0.35)', marginBottom:'10px', fontWeight:600 }}>
            Aperçu en direct
          </div>
          <BrandingPreview branding={branding} userName={user?.prenom || user?.full_name || 'Aytekin'} />
        </div>
      </div>
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════════════
// Sous-composants
// ════════════════════════════════════════════════════════════════════════════
function Card({ title, children }) {
  return (
    <div style={{
      background:'#fff',
      border:'0.5px solid rgba(0,0,0,0.07)',
      borderRadius:'12px',
      padding:'14px 16px',
      boxShadow:'0 1px 3px rgba(0,0,0,0.04)',
    }}>
      <div style={{ fontSize:'11px', textTransform:'uppercase', letterSpacing:'0.06em', color:'#8b8576', marginBottom:'10px', fontWeight:600 }}>
        {title}
      </div>
      {children}
    </div>
  );
}

function Label({ children }) {
  return <div style={{ fontSize:'11px', color:'rgba(26,25,22,0.55)', marginBottom:'4px', fontWeight:500 }}>{children}</div>;
}
function Hint({ children }) {
  return <div style={{ fontSize:'10.5px', color:'#9a8f7a', marginTop:'3px', fontStyle:'italic' }}>{children}</div>;
}

const inputStyle = {
  width:'100%', boxSizing:'border-box',
  padding:'7px 10px', borderRadius:'6px',
  border:'1px solid rgba(0,0,0,0.12)',
  fontSize:'12.5px', color:'#1A1916',
  fontFamily:'inherit', outline:'none',
};
const selectStyle = { ...inputStyle, cursor:'pointer', background:'#fff' };
const uploadBtn = {
  display:'inline-block',
  background:'#fff', border:'0.5px solid rgba(0,0,0,0.15)',
  color:'#1A1916', padding:'5px 12px', borderRadius:'5px',
  fontSize:'11.5px', cursor:'pointer', fontFamily:'inherit',
};
const removeBtn = {
  background:'transparent', border:'0.5px solid rgba(176,48,32,0.25)',
  color:'#B03020', padding:'5px 12px', borderRadius:'5px',
  fontSize:'11.5px', cursor:'pointer', fontFamily:'inherit',
};

function ColorField({ label, value, onChange, hint }) {
  const valid = isValidHex(value);
  return (
    <div>
      <Label>{label}</Label>
      <div style={{
        display:'flex', alignItems:'center', gap:'6px',
        background:'#fff', border:'1px solid rgba(0,0,0,0.12)',
        borderRadius:'6px', padding:'4px',
      }}>
        <input
          type="color"
          value={valid ? value : '#000000'}
          onChange={e => onChange(e.target.value)}
          style={{ width:'32px', height:'30px', border:'none', borderRadius:'4px', cursor:'pointer', background:'transparent', padding:0 }}
        />
        <input
          type="text"
          value={value}
          onChange={e => onChange(e.target.value)}
          placeholder="#000000"
          style={{ flex:1, minWidth:0, border:'none', outline:'none', fontSize:'12px', fontFamily:'monospace', color: valid ? '#1A1916' : '#B03020', textTransform:'uppercase' }}
        />
      </div>
      {hint && <Hint>{hint}</Hint>}
    </div>
  );
}

// ── Aperçu live : maquette d'interface qui réagit aux changements ──
function BrandingPreview({ branding, userName }) {
  const primary   = isValidHex(branding.couleur_primaire)   ? branding.couleur_primaire   : '#B8860B';
  const secondary = isValidHex(branding.couleur_secondaire) ? branding.couleur_secondaire : '#1A1916';
  const accent    = isValidHex(branding.couleur_accent)     ? branding.couleur_accent     : '#1A55A0';
  const primaryFg = getContrastTextColor(primary);
  const accentFg  = getContrastTextColor(accent);
  const fontTitle = `'${branding.font_titre || 'Syne'}', sans-serif`;
  const fontBody  = `'${branding.font_corps || 'DM Sans'}', sans-serif`;
  const brandName = branding.nom_commercial || 'Mon Cabinet';

  return (
    <div style={{
      background:'#fff', borderRadius:'12px',
      border:'0.5px solid rgba(0,0,0,0.07)',
      boxShadow:'0 8px 24px rgba(0,0,0,0.06)',
      overflow:'hidden',
      fontFamily: fontBody,
    }}>
      {/* Mini navbar avec logo */}
      <div style={{
        background: secondary,
        color: getContrastTextColor(secondary),
        padding:'10px 14px',
        display:'flex', alignItems:'center', justifyContent:'space-between',
      }}>
        <div style={{ display:'flex', alignItems:'center', gap:'10px' }}>
          {branding.logo_url ? (
            <img src={branding.logo_url} alt="" style={{ height:'24px', maxWidth:'80px', objectFit:'contain' }} />
          ) : (
            <div style={{
              width:'24px', height:'24px', borderRadius:'5px',
              background: primary, color: primaryFg,
              display:'inline-flex', alignItems:'center', justifyContent:'center',
              fontSize:'12px', fontWeight:700,
            }}>{brandName.charAt(0).toUpperCase()}</div>
          )}
          <span style={{ fontFamily: fontTitle, fontWeight:600, fontSize:'14px' }}>{brandName}</span>
        </div>
        <div style={{
          width:'24px', height:'24px', borderRadius:'50%',
          background:'rgba(255,255,255,0.15)',
          display:'inline-flex', alignItems:'center', justifyContent:'center',
          fontSize:'10px', fontWeight:600,
        }}>{(userName || 'A').charAt(0).toUpperCase()}</div>
      </div>

      {/* Corps avec titres + boutons */}
      <div style={{ padding:'18px 16px' }}>
        <div style={{ fontFamily: fontTitle, fontSize:'18px', fontWeight:600, color: secondary, marginBottom:'2px' }}>
          Bonjour {userName} 👋
        </div>
        <div style={{ fontSize:'12px', color:'rgba(26,25,22,0.45)', marginBottom:'14px' }}>
          Voici votre espace {brandName}.
        </div>

        {/* KPIs */}
        <div style={{ display:'grid', gridTemplateColumns:'repeat(3, 1fr)', gap:'8px', marginBottom:'14px' }}>
          {[
            { l:'Dossiers actifs', v:'17', c: primary },
            { l:'Terminés',        v:'9',  c: '#1A7A4A' },
            { l:'À traiter',       v:'2',  c: '#B03020' },
          ].map((k, i) => (
            <div key={i} style={{ background:'#fafaf8', borderRadius:'7px', padding:'8px 10px' }}>
              <div style={{ fontSize:'9px', textTransform:'uppercase', letterSpacing:'0.07em', color:'rgba(26,25,22,0.4)', marginBottom:'2px' }}>{k.l}</div>
              <div style={{ fontFamily: fontTitle, fontSize:'18px', fontWeight:700, color: k.c }}>{k.v}</div>
            </div>
          ))}
        </div>

        {/* Liste */}
        <div style={{ background:'#fafaf8', borderRadius:'7px', padding:'10px', marginBottom:'12px' }}>
          {[
            { name:'AZAD (EX:SIVRI)', status:'Transmis au GTC', sc:'#dbeafe', fc:'#1e40af' },
            { name:'MY FIRST ASSURANCE', status:'Kbis reçu',     sc:'#d1fae5', fc:'#047857' },
            { name:'HARMONIE',            status:'En attente',    sc:'#fef3c7', fc:'#92400e' },
          ].map((d, i) => (
            <div key={i} style={{ display:'flex', justifyContent:'space-between', alignItems:'center', padding:'6px 0', borderBottom: i < 2 ? '0.5px solid rgba(0,0,0,0.06)' : 'none' }}>
              <span style={{ fontSize:'11.5px', color:'#1A1916', fontWeight:500 }}>{d.name}</span>
              <span style={{ fontSize:'10px', fontWeight:500, padding:'2px 7px', borderRadius:'4px', background:d.sc, color:d.fc }}>{d.status}</span>
            </div>
          ))}
        </div>

        {/* Boutons primary + accent */}
        <div style={{ display:'flex', gap:'6px' }}>
          <button style={{
            background: primary, color: primaryFg, border:'none',
            padding:'7px 14px', borderRadius:'6px', fontSize:'11.5px', fontWeight:500,
            fontFamily: fontBody, cursor:'pointer', flex:1,
          }}>+ Nouveau dossier</button>
          <button style={{
            background:'transparent', color: accent,
            border:`0.5px solid ${accent}`,
            padding:'7px 14px', borderRadius:'6px', fontSize:'11.5px', fontWeight:500,
            fontFamily: fontBody, cursor:'pointer',
          }}>Tout voir</button>
        </div>

        {/* Footer */}
        {!branding.hide_lexback_branding && (
          <div style={{ marginTop:'14px', textAlign:'center', fontSize:'9.5px', color:'rgba(26,25,22,0.3)' }}>
            Propulsé par <span style={{ color:'#B8860B', fontWeight:500 }}>Lexback</span>
          </div>
        )}
      </div>

      {/* Carte "Email envoyé" */}
      <div style={{ background:'#fafaf8', borderTop:'0.5px solid rgba(0,0,0,0.05)', padding:'12px 16px' }}>
        <div style={{ fontSize:'10px', textTransform:'uppercase', letterSpacing:'0.06em', color:'rgba(26,25,22,0.4)', marginBottom:'8px', fontWeight:600 }}>
          Aperçu d'email envoyé à vos clients
        </div>
        <div style={{ background:'#fff', border:'0.5px solid rgba(0,0,0,0.07)', borderRadius:'7px', padding:'12px' }}>
          <div style={{ fontFamily: fontTitle, fontSize:'13px', color: secondary, fontWeight:600, marginBottom:'6px' }}>
            Votre dossier a bien été reçu
          </div>
          <div style={{ fontSize:'11.5px', color:'#1A1916', lineHeight:1.5, marginBottom:'10px' }}>
            Bonjour,<br/>
            Nous accusons réception de votre dossier. <a href="#" onClick={e => e.preventDefault()} style={{ color: accent, textDecoration:'underline' }}>Suivre l'avancement</a>.
          </div>
          <div style={{ fontSize:'10.5px', color:'rgba(26,25,22,0.5)', whiteSpace:'pre-line', borderTop:'0.5px solid rgba(0,0,0,0.06)', paddingTop:'8px' }}>
            {branding.signature_email || `L'équipe ${brandName}`}
            {branding.telephone_support && `\n📞 ${branding.telephone_support}`}
            {branding.url_site && `\n🌐 ${branding.url_site}`}
          </div>
        </div>
      </div>
    </div>
  );
}
